import { Info } from "lucide-react";
import moment from "moment/moment";
import { Badge } from "@/components/ui";
import { getErrorMessage, urlWithFormat, useSocialCoachSession, useSocialNetworks } from "@/utils";
import { SocialNetworksSection } from "@/components/SocialNetworksSection";
import * as React from "react";
import { VideoProjectDetail, VideoProjectStatus } from "@/interfaces";
import { VideoScriptModal } from "../VideoScriptModal";
import { UsersServices, User, VideoProject, VideoProjectServices } from "@/services/bedrock";
import {
  VideoProjectStaffPicker,
  assignedUserIdForRole,
  assigningRoleForEvent,
  canUnassignStaff,
  confirmEventSamePersonAssignment,
  recommendedStaffForRole,
  resolveStaffActorUserId,
  resolveStaffUser,
} from "@/components/VideoProjectStaffPicker";
import { useVideoProjectRejectQaDialog } from "@/components/VideoProjectRejectQaDialog";
import {
  VideoProjectStatusColumnId,
  VideoProjectStatusPicker,
  getVideoProjectEventForStatusChange,
} from "@/components/VideoProjectStatusPicker";
import { useInvalidateVideoProjectEventLogs } from "../VideoProjectHistoryPanel";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ProjectInfoCardProps {
  videoProject: VideoProjectDetail;
  setVideoProject: React.Dispatch<React.SetStateAction<VideoProjectDetail | undefined>>;
}

const ASSIGNMENT_EXTRA_FIELDS = { queueAssignments: ["staffUser"] } as const;
const assignmentQueryKey = (id: number) => ["videoProjectAssignments", id] as const;

export function ProjectInfoCard({ videoProject, setVideoProject }: ProjectInfoCardProps) {
  const { userId, isAdmin } = useSocialCoachSession();
  const currentUserId = resolveStaffActorUserId(userId);
  const queryClient = useQueryClient();
  const invalidateEventLogs = useInvalidateVideoProjectEventLogs();
  const { requestIssues: requestRejectQaIssues, dialog: rejectQaDialog } = useVideoProjectRejectQaDialog();

  const [openVideoScriptModal, setOpenVideoScriptModal] = React.useState<boolean>(false);
  const [savingEditor, setSavingEditor] = React.useState<boolean>(false);
  const [savingQaTester, setSavingQaTester] = React.useState<boolean>(false);
  const [savingStatus, setSavingStatus] = React.useState<boolean>(false);

  const { data: editorsData } = useQuery({
    queryKey: ["editors"],
    queryFn: () => UsersServices.getUsers({ userType: User.Type.EDITOR }),
    staleTime: 1000 * 60 * 30,
  });
  const staffUsers = editorsData?.content || [];

  const videoProjectId = videoProject.videoProjectId;
  const { data: assignmentProject, isPending: assignmentPending } = useQuery({
    queryKey: assignmentQueryKey(videoProjectId!),
    queryFn: () => VideoProjectServices.show(videoProjectId!, ASSIGNMENT_EXTRA_FIELDS),
    enabled: videoProjectId != null,
  });
  const requiresQa = assignmentProject?.requiresQa ?? videoProject.requiresQa;

  const postSnIds = React.useMemo(() => {
    return videoProject.post?.socialNetworks?.filter(sn => sn.status === "POSTED").map(sn => sn.socialNetworkId);
  }, [videoProject.post?.socialNetworks]);

  const postSnUrls = React.useMemo(() => {
    let snUrls = {};

    videoProject.post?.socialNetworks?.map(sn => {
      if (sn.externalPostUrl && sn.socialNetworkId !== "FB") {
        snUrls = {
          ...snUrls,
          [sn.socialNetworkId]: urlWithFormat(sn.externalPostUrl!.replace("[", "").replace("]", "")),
        };
      } else {
        snUrls = {
          ...snUrls,
          [sn.socialNetworkId]: sn.externalPostUrl!,
        };
      }
    });
    return snUrls;
  }, [videoProject.post?.socialNetworks]);

  const editorUserId = assignmentProject?.editorUserId ?? videoProject.editorUserId;
  const qaUserId = assignmentProject?.qaUserId ?? null;
  const projectStatus = (assignmentProject?.status ?? videoProject.status) as VideoProject.Status;
  const archived = assignmentProject?.archived ?? videoProject.archived;
  const assignments = assignmentProject?.queueAssignments ?? [];
  const staffProject = {
    editorUserId,
    qaUserId,
    queueAssignments: assignments,
  } as VideoProject;
  const editor = resolveStaffUser(editorUserId, staffUsers, assignments);
  const qaUser = resolveStaffUser(qaUserId, staffUsers, assignments);
  const recommendedEditors = recommendedStaffForRole(staffProject, "EDITOR", staffUsers);
  const recommendedQa = recommendedStaffForRole(staffProject, "QA", staffUsers);

  const cacheUpdatedProject = (updated: VideoProject) => {
    if (videoProjectId == null) return;
    queryClient.setQueryData<VideoProject>(assignmentQueryKey(videoProjectId), prev =>
      prev ? { ...prev, ...updated, queueAssignments: updated.queueAssignments ?? prev.queueAssignments } : updated
    );
  };

  const applyAssignmentUpdate = (updated: VideoProject, role: VideoProject.QueueAssignment["role"]) => {
    cacheUpdatedProject(updated);
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        ...(role === "EDITOR" ? { editorUserId: updated.editorUserId ?? undefined } : {}),
        ...(updated.status ? { status: String(updated.status) as VideoProjectStatus } : {}),
      };
    });
  };

  const handleStaffChange = async (role: VideoProject.QueueAssignment["role"], userId: number | null) => {
    const isEditor = role === "EDITOR";
    const roleLabel = isEditor ? "Editor" : "QA Tester";
    if (videoProjectId == null) return;
    const currentId = isEditor ? editorUserId : qaUserId;
    if ((userId ?? null) === (currentId ?? null)) return;
    if (userId == null && !canUnassignStaff(role, projectStatus)) {
      toast.error(`${roleLabel} can't be unassigned after ${isEditor ? "editing" : "testing"} has started`);
      return;
    }

    const setSaving = isEditor ? setSavingEditor : setSavingQaTester;
    setSaving(true);
    try {
      const latest = await VideoProjectServices.show(videoProjectId, ASSIGNMENT_EXTRA_FIELDS);
      const assign = () =>
        VideoProjectServices.update(
          videoProjectId,
          {
            event: VideoProject.Event.ASSIGN,
            role,
            fromUserId: (isEditor ? latest.editorUserId : latest.qaUserId) ?? null,
            toUserId: userId,
          },
          ASSIGNMENT_EXTRA_FIELDS
        );
      const updated = await assign();
      applyAssignmentUpdate(updated, role);
      invalidateEventLogs(videoProjectId);
      toast.success(`${roleLabel} updated successfully!`);
    } catch (e) {
      console.error(`Error updating ${roleLabel}`, e);
      toast.error(getErrorMessage(e) || `Failed to update ${roleLabel}`);
    } finally {
      setSaving(false);
    }
  };

  const applyStatusUpdate = (updated: VideoProject) => {
    cacheUpdatedProject(updated);
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        editorUserId: updated.editorUserId ?? prev.editorUserId,
        ...(updated.status ? { status: String(updated.status) as VideoProjectStatus } : {}),
      };
    });
  };

  const handleStatusChange = async (columnId: VideoProjectStatusColumnId) => {
    if (videoProjectId == null || assignmentPending) return;

    const request = getVideoProjectEventForStatusChange(projectStatus, columnId, {
      requiresQa,
      currentUserId,
    });
    if (!request) {
      toast.error("Cannot move project to this status");
      return;
    }

    const role = assigningRoleForEvent(request.event);
    const userId = role
      ? assignedUserIdForRole({ editorUserId, qaUserId }, role) ?? request.userId ?? currentUserId
      : request.userId;
    if (role && userId == null) {
      toast.error("Cannot move project to this status");
      return;
    }

    const confirmed = await confirmEventSamePersonAssignment(
      { editorUserId, qaUserId },
      request.event,
      userId,
      staffUsers
    );
    if (!confirmed) return;

    let issues: VideoProject.QaIssue[] | undefined;
    if (request.event === VideoProject.Event.REJECT_QA) {
      const selectedIssues = await requestRejectQaIssues();
      if (!selectedIssues) return;
      issues = selectedIssues;
    }

    setSavingStatus(true);
    try {
      const updated = await VideoProjectServices.update(
        videoProjectId,
        {
          event: request.event,
          ...(request.event === VideoProject.Event.START_EDITING && userId != null ? { editorUserId: userId } : {}),
          ...(issues ? { issues } : {}),
        },
        ASSIGNMENT_EXTRA_FIELDS
      );
      applyStatusUpdate(updated);
      invalidateEventLogs(videoProjectId);
      toast.success("Status updated successfully!");
    } catch (e) {
      console.error("Error updating status", e);
      toast.error("Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const { postableSocialNetworks } = useSocialNetworks();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row justify-between items-center w-full select-none">
        <label className="sectionHeader font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <Info className="size-4.5 text-blue-600" />
          Project Info
        </label>
      </div>
      <div className="videoProjectContainer topContainer flex flex-col gap-4 bg-slate-50/50 p-5 rounded-xl border border-slate-200">
        <div className="projectInfo flex flex-col gap-3.5 text-sm text-slate-600">
          <div className="flex flex-row justify-between items-center flex-wrap gap-2">
            <span className="font-medium text-slate-500">Status:</span>
            <div className="flex flex-row items-center gap-2">
              {savingStatus && (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-indigo-600 border-t-transparent shrink-0" />
              )}
              <VideoProjectStatusPicker
                compact
                labeled
                status={projectStatus}
                archived={!!archived}
                requiresQa={requiresQa}
                currentUserId={currentUserId}
                disabled={savingStatus || savingEditor || assignmentPending}
                onSelect={columnId => void handleStatusChange(columnId)}
              />
              {videoProject.revisionCount !== undefined &&
                videoProject.revisionCount !== null &&
                videoProject.revisionCount > 0 && (
                  <Badge
                    variant="outline"
                    className={cn(
                      "font-semibold text-xs py-0.5 px-2 rounded-full border shadow-2xs select-none",
                      videoProject.revisionCount === 1
                        ? "bg-amber-100 text-amber-900 border-amber-300"
                        : videoProject.revisionCount === 2
                        ? "bg-orange-200 text-orange-900 border-orange-400"
                        : "bg-red-200 text-red-900 border-red-400"
                    )}
                  >
                    Revisions: {videoProject.revisionCount}
                  </Badge>
                )}
            </div>
          </div>
          <div className="flex flex-row justify-between items-center flex-wrap gap-2">
            <span className="font-medium text-slate-500">Status Date:</span>
            <span className="text-slate-400 text-xs font-medium">
              {moment(videoProject?.statusDate).format("MMM D, YYYY [at] h:mm a")}
            </span>
          </div>
          <div className="flex flex-row justify-between items-center flex-wrap gap-2">
            <span className="font-medium text-slate-500">Created:</span>
            <span className="text-slate-400 text-xs font-medium">
              {moment(videoProject?.createdDate).format("MMM D, YYYY [at] h:mm a")}
            </span>
          </div>
          <div className="flex flex-row justify-between items-center flex-wrap gap-2">
            <span className="font-medium text-slate-500">Editor:</span>
            <div className="flex items-center gap-2">
              {savingEditor && (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-indigo-600 border-t-transparent shrink-0" />
              )}
              <VideoProjectStaffPicker
                role="EDITOR"
                compact
                labeled
                selectedUserId={editorUserId}
                selectedUser={editor}
                otherRoleUserId={qaUserId}
                recommendedUsers={recommendedEditors}
                staffUsers={staffUsers}
                currentUserId={currentUserId}
                disabled={savingEditor}
                allowUnassign={canUnassignStaff("EDITOR", projectStatus)}
                onSelect={userId => handleStaffChange("EDITOR", userId)}
              />
            </div>
          </div>
          {isAdmin && (
            <div className="flex flex-row justify-between items-center flex-wrap gap-2">
              <span className="font-medium text-slate-500">QA Tester:</span>
              <div className="flex items-center gap-2">
                {savingQaTester && (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-indigo-600 border-t-transparent shrink-0" />
                )}
                <VideoProjectStaffPicker
                  role="QA"
                  compact
                  labeled
                  selectedUserId={qaUserId}
                  selectedUser={qaUser}
                  otherRoleUserId={editorUserId}
                  recommendedUsers={recommendedQa}
                  staffUsers={staffUsers}
                  currentUserId={currentUserId}
                  disabled={savingQaTester}
                  allowUnassign={canUnassignStaff("QA", projectStatus)}
                  onSelect={userId => handleStaffChange("QA", userId)}
                />
              </div>
            </div>
          )}
          {videoProject.videoScript && (
            <div className="flex flex-row justify-between items-center flex-wrap gap-2">
              <span className="font-medium text-slate-500">Script:</span>
              <span
                className="text-primary font-semibold hover:underline cursor-pointer"
                onClick={() => setOpenVideoScriptModal(true)}
              >
                {videoProject.videoScript.title}
              </span>
              <VideoScriptModal
                open={openVideoScriptModal}
                videoScript={videoProject.videoScript}
                onClose={() => setOpenVideoScriptModal(false)}
              />
            </div>
          )}
          {postSnIds && postSnIds.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <SocialNetworksSection
                actives={postSnIds}
                links={postSnUrls}
                allSocialNetworks={postableSocialNetworks}
              />
            </div>
          )}
        </div>
      </div>
      {rejectQaDialog}
    </div>
  );
}
